import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Inbox, RotateCcw } from 'lucide-react'
import { NOTIFICATION_CHANNELS, NOTIFICATION_EVENTS, NOTIFICATION_STATUSES } from '@concours/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  Empty,
  FilterRow,
  PageHeader,
  Pagination,
  Select,
  Stat,
  StatGrid,
  Table,
  useAuth,
  useToast,
  type Column,
} from '@concours/ui'
import { errorMessage } from '../../lib/errors'
import { when } from '../../lib/competition-types'
import {
  NOTIFICATIONS_KEY,
  STATUS_VARIANT,
  useHistory,
  type HistoryFilters,
  type HistoryRow,
} from '../../lib/notifications'

const options = (o: Record<string, string>, all: string) => [
  { value: '', label: all },
  ...Object.entries(o).map(([value, label]) => ({ value, label })),
]

const EVENT_OPTIONS = options(
  {
    ...Object.fromEntries(Object.entries(NOTIFICATION_EVENTS).map(([k, v]) => [k, v.label])),
    campaign: 'Envoi groupé',
  },
  'Tous les événements'
)

/**
 * Historique des envois (cahier §12) : statut par canal, tentatives, erreur,
 * fournisseur ; destinataire masqué. Relance d'un courriel / SMS en échec.
 */
export function HistoryPage() {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<HistoryFilters>({ page: 1 })
  const history = useHistory(filters)
  const retry = useMutation({
    mutationFn: (id: string) => session.api.post<HistoryRow>(`/admin/notifications/${id}/retry`),
    onSuccess: async (row) => {
      if (row.status === 'sent') toast.success('Notification envoyée')
      else toast.show({ title: 'Nouvel essai en cours', tone: 'info' })
      await queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY })
    },
    onError: (error) => toast.error(errorMessage(error)),
  })
  const set = (key: keyof HistoryFilters, value: string) =>
    setFilters((f) => ({ ...f, [key]: value || undefined, page: 1 }))
  const counts = history.data?.counts ?? {}

  const columns: Column<HistoryRow>[] = [
    { key: 'date', header: 'Date', cell: (r) => when(r.createdAt) },
    {
      key: 'event',
      header: 'Événement',
      cell: (r) => (
        <span className="stack" style={{ gap: 2 }}>
          <strong>{r.eventLabel}</strong>
          <span className="muted">{r.title}</span>
        </span>
      ),
    },
    {
      key: 'channel',
      header: 'Canal',
      cell: (r) => (
        <span className="stack" style={{ gap: 2 }}>
          <span>{r.channelLabel}</span>
          {r.recipient && <span className="mono muted">{r.recipient}</span>}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Statut',
      cell: (r) => (
        <span className="stack" style={{ gap: 2 }}>
          <Badge variant={STATUS_VARIANT[r.status]}>{r.statusLabel}</Badge>
          {r.lastError && <span className="muted">{r.lastError}</span>}
          {r.channel === 'portal' && <span className="muted">{r.read ? 'Lue' : 'Non lue'}</span>}
        </span>
      ),
    },
    {
      key: 'attempts',
      header: 'Essais',
      numeric: true,
      cell: (r) => (r.channel === 'portal' ? '—' : r.attempts),
    },
    { key: 'competition', header: 'Concours', cell: (r) => r.competition ?? '—' },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'end',
      cell: (r) =>
        r.status === 'failed' && (
          <Button
            size="sm"
            variant="secondary"
            icon={<RotateCcw />}
            loading={retry.isPending && retry.variables === r.id}
            onClick={() => retry.mutate(r.id)}
          >
            Relancer
          </Button>
        ),
    },
  ]

  return (
    <>
      <PageHeader
        eyebrow="Communication"
        title="Historique des notifications"
        description="Chaque envoi, par destinataire et par canal. Les adresses et numéros sont masqués."
      />
      <StatGrid>
        {(['sent', 'pending', 'failed', 'skipped'] as const).map((s) => (
          <Stat key={s} label={NOTIFICATION_STATUSES[s]} value={counts[s] ?? 0} />
        ))}
      </StatGrid>
      {history.isError && <Alert tone="danger">{errorMessage(history.error)}</Alert>}
      <Card>
        <FilterRow>
          <Select
            label="Événement"
            options={EVENT_OPTIONS}
            value={filters.event ?? ''}
            onChange={(e) => set('event', e.target.value)}
          />
          <Select
            label="Canal"
            options={options(NOTIFICATION_CHANNELS, 'Tous les canaux')}
            value={filters.channel ?? ''}
            onChange={(e) => set('channel', e.target.value)}
          />
          <Select
            label="Statut"
            options={options(NOTIFICATION_STATUSES, 'Tous les statuts')}
            value={filters.status ?? ''}
            onChange={(e) => set('status', e.target.value)}
          />
        </FilterRow>
        <Table
          columns={columns}
          rows={history.data?.data ?? []}
          rowKey={(r) => r.id}
          loading={history.isPending}
          caption="Historique des notifications"
          empty={
            <Empty
              icon={<Inbox aria-hidden="true" />}
              title="Aucune notification"
              description="Aucun envoi ne correspond à ces filtres."
            />
          }
        />
        {history.data && history.data.meta.total > history.data.meta.perPage && (
          <Pagination
            page={filters.page}
            perPage={history.data.meta.perPage}
            total={history.data.meta.total}
            onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
          />
        )}
      </Card>
    </>
  )
}
